import { inject, injectable, interfaces } from 'inversify';
import { debounce } from 'lodash';
import { InputStatus } from '../../../../../../__legacy__/models/error.model';
import { AnnotationToolType } from '../../../../../../__legacy__/modules/annotation/submodules/annotationTypes/models/annotationToolType';
import { IEventBus } from '../../../../../../__legacy__/services/eventBus.service';
import { CheckMarkingToolNameUniquenessRequest, ICheckMarkingToolNameUniquenessService } from '../commons/services/CheckMarkingToolNameUniqueness.service';
import { IEditMarkingToolStore } from './EditMarkingTool.store';
import { MarkingToolEditedEvent } from './events/MarkingToolEditedEvent';
import { EditMarkingToolRequest, IEditMarkingToolApiService } from './services/EditMarkingToolApi.service';
import { GetMarkingToolRequest, IGetMarkingToolService } from './services/GetMarkingTool.service';

export interface IEditMarkingToolBl {
  editMarkingToolAsync: () => Promise<void>;
  hideModal: () => void;
  showModalAsync: (projectId: string, markingTool: string) => Promise<void>;
  changeName: (newName: string) => void;
  changeToolType: (newType: AnnotationToolType) => void;
  changeColor: (newColor: string) => void;
}

export namespace IEditMarkingToolBl {
  export const $: interfaces.ServiceIdentifier<IEditMarkingToolBl> = Symbol('EDIT_MARKING_TOOL_BL');
}

@injectable()
export class EditMarkingToolBlImpl implements IEditMarkingToolBl {
  constructor(
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(IEditMarkingToolStore.$) private readonly store: IEditMarkingToolStore,
    @inject(IEditMarkingToolApiService.$) readonly editFormApiService: IEditMarkingToolApiService,
    @inject(ICheckMarkingToolNameUniquenessService.$) readonly checkUniquenessService: ICheckMarkingToolNameUniquenessService,
    @inject(IGetMarkingToolService.$) private readonly getAnnotationTypeService: IGetMarkingToolService
  ) {}

  public editMarkingToolAsync = async (): Promise<void> => {
    await this.validateNameAsync(this.store.name);
    if (!this.store.nameStatus.isValid) {
      return;
    }

    const request = new EditMarkingToolRequest(this.store.name, this.store.markingToolId, this.store.color, this.store.toolType);
    await this.editFormApiService.editMarkingToolAsync(request);

    this.eventBus.sendEvent(new MarkingToolEditedEvent());

    this.hideModal();
  };

  public hideModal = (): void => {
    this.store.setShowModal(false);
  };

  public changeName = (newName: string): void => {
    this.store.setName(newName);
    void this.validateNameDebounced(newName);
  };

  public changeToolType = (newType: AnnotationToolType): void => {
    this.store.setToolType(newType);
  };

  public changeColor = (newColor: string): void => {
    this.store.setColor(newColor);
  };

  public showModalAsync = async (projectId: string, markingToolId: string): Promise<void> => {
    const request = new GetMarkingToolRequest(projectId, markingToolId);
    const response = await this.getAnnotationTypeService.getMarkingToolAsync(request);

    this.store.setupStore(projectId, response.model);
  };

  private validateNameAsync = async (newName: string): Promise<void> => {
    const nameStatus = InputStatus.valid();

    if (newName === '') {
      nameStatus.addErrorCode('field_cant_be_empty');
    }

    if (newName === 'Image') {
      nameStatus.addErrorCode('annotation_type_name_cannot_be_image');
    }

    const request = new CheckMarkingToolNameUniquenessRequest(this.store.projectId, newName, this.store.markingToolId);
    if (!(await this.checkUniquenessService.checkMarkingToolNameUniqueness(request))) {
      nameStatus.addErrorCode('annotation_tool_name_must_be_unique');
    }

    this.store.setNameStatus(nameStatus);
  };

  private validateNameDebounced = debounce(async (name: string) => {
    await this.validateNameAsync(name);
  }, 300);
}
